import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React, { useContext, useMemo, useState } from 'react';
import { t, translate } from '../../../../types/translation/Translator';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { ProductTransactionContext } from '../../../../context/ProductTransactionContext';
import { UserContext } from '../../../../context/UserContext';
import { CompanyContext } from '../../../../context/CompanyContext';
import { ContainerContext } from '../../../../context/ContainerContext';
import { OrderContext } from '../../../../context/OrderContext';
import { StockLocationContext } from '../../../../context/StockLocationContext';
import { TagRelationContext } from '../../../../context/TagRelationContext';
import {
  ProductTransaction,
  ProductTransactionParentType,
  productTransactionParentTypeToString,
  ProductTransactionStatus,
  ProductTransactionType,
} from '../../../../types/productTransaction';
import { Grid } from '@mui/material';
import { UnitOfMeasure } from '../../../../types/uom';
import { iconForType } from '../../../../util/productTransactions.util';
import { MenuItemProps, SettingsMenuItemTemplate } from '../../../../VentoryUI/components/common/Menu';
import ExportIcon from '../../../../VentoryUI/icons/Export/ExportIcon';
import ImportIcon from '../../../../VentoryUI/icons/Import/ImportIcon';
import { ProductTransactionExportModal } from './ProductTransactionExportModal';
import ProductTransactionDetailModal from '../Product/Modals/ProductTransactionDetailModal';
import FlexPane from '../../../../VentoryUI/components/common/FlexPane';
import SearchBarWithFilter from '../../../../VentoryUI/components/common/SearchBarWithFilter';
import { ProductTransactionFilter } from '../../../../VentoryUI/components/filters/ProductTransactionFilter';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import Table from '../../../components/Table';
import { Routing } from '../../../../util/routes.util';
import { TaskContext } from '../../../../context/TaskContext';
import { ContainerTypeContext } from '../../../../context/ContainerTypeContext';
import { DateTimeUtil } from '../../../../util/date.util';
import dayjs from 'dayjs';
import { Elteros } from '../../../../types/customer/Elteros/Elteros';
import CheckIcon from '../../../../VentoryUI/icons/Check/CheckIcon';

export default function ProductHistoryOverviewPane() {
  const navigate = useNavigate();

  const [params] = useSearchParams();

  const { currentCompany, companies } = useContext(CompanyContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { containers } = useContext(ContainerContext);
  const { containerTypes } = useContext(ContainerTypeContext);
  const { orders, ordersLoading, loadAllOrders } = useContext(OrderContext);
  const { productTransactions, productTransactionsLoading, loadAllProductTransactions } =
    useContext(ProductTransactionContext);
  const { companyUsers, locale } = useContext(UserContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { tasks } = useContext(TaskContext);

  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<ProductTransaction | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);

  const items = useMemo(
    () =>
      [...productTransactions.values()]
        .filter(a => a.parentType !== ProductTransactionParentType.transaction)
        .sort(
          (a, b) => new Date(b.processedAt || b.createdAt).getTime() - new Date(a.processedAt || a.createdAt).getTime(),
        ),
    [productTransactions],
  );
  const [filteredItems, setFilteredItems] = useState<ProductTransaction[]>(items);

  const getElterosSpecificStockLocation = (item: ProductTransaction) => {
    if (item.type === ProductTransactionType.inbound) {
      return stockLocations.get(item.product.toStockLocationId || '')?.name || '';
    } else if (item.type === ProductTransactionType.outbound) {
      if (item.parentType === ProductTransactionParentType.order && item.parentId) {
        const order = orders.get(item.parentId);
        if (order) {
          const projekt = order.customFields.get('08281bc5-cab0-4a89-a0fa-9327969e5f10');
          if (projekt) return projekt.value;
        }
      }

      return stockLocations.get(item.product.fromStockLocationId || '')?.name || '';
    }
    return '';
  };

  const isPartner = currentCompany.partner;

  const headers = [
    {
      key: 'company',
      name: t(TranslationKey.company).singular.label,
      text: (item: ProductTransaction) => companies.get(item.companyId)?.name || '',
    },
    {
      key: 'processedAt',
      name: t(TranslationKey.processedAt).singular.label,
      text: (item: ProductTransaction) =>
        DateTimeUtil.toLocaleDateTimeString(locale, dayjs(item.processedAt || item.createdAt).toDate()),
      sortValue: (item: ProductTransaction) => item.processedAt || item.createdAt,
      defaultSort: true,
    },
    {
      key: 'processedBy',
      name: t(TranslationKey.processedBy).singular.label,
      text: (item: ProductTransaction) => {
        return item.metadata?.user?.email || '';
      },
    },
    {
      key: 'number',
      name: t(TranslationKey.productNumber).singular.label,
      text: (item: ProductTransaction) => {
        if (item.product.pmdId) {
          return (
            productMasterData.get(item.product.pmdId ?? '')?.productNumber ||
            t(TranslationKey.unknownProduct).singular.label
          );
        }

        if (item.product.containerId) {
          const container = containers.get(item.product.containerId);
          if (!container) return t(TranslationKey.unknownContainer).singular.label;

          const containerType = containerTypes.get(container.containerTypeId);
          if (!containerType) return t(TranslationKey.unknownContainerType).singular.label;

          return containerType.name;
        }

        return '';
      },
    },
    {
      key: 'action',
      name: t(TranslationKey.actions).singular.label,
      text: (item: ProductTransaction) =>
        item.parentType === ProductTransactionParentType.order && item.parentId
          ? `${orders.get(item.parentId)?.number || t(TranslationKey.order).singular.label}`
          : item.parentType === ProductTransactionParentType.task && item.parentId
          ? `${t(TranslationKey.task).singular.label} ${
              tasks.get(item.parentId)?.number || t(TranslationKey.task).singular.label
            }`
          : productTransactionParentTypeToString(item.parentType),
    },
    {
      key: 'sourceStockLocation',
      name:
        currentCompany.id === Elteros.companyId ? 'Projektas' : translate({ key: TranslationKey.sourceStockLocation }),
      text: (item: ProductTransaction) => {
        // This is really dirty and should be fixed!!
        if (currentCompany.id === Elteros.companyId) return getElterosSpecificStockLocation(item);

        return stockLocations.get(item.product.fromStockLocationId || '')?.name || '';
      },
    },
    {
      key: 'destinationStockLocation',
      name: translate({ key: TranslationKey.destinationStockLocation }),
      text: (item: ProductTransaction) => {
        return stockLocations.get(item.product.toStockLocationId || '')?.name || '';
      },
    },
    {
      key: 'quantity',
      name: t(TranslationKey.quantity).singular.label,
      text: (item: ProductTransaction) => (
        <Grid container columnSpacing={1} justifyContent={'flex-end'}>
          {item.status === ProductTransactionStatus.confirmed && (
            <Grid item marginY={'auto'} className='w-7'>
              <CheckIcon color='green' />
            </Grid>
          )}
          <Grid item marginY={'auto'} className='h-7'>
            <p
              title={item.product.quantity.toString()}
              className={`h-7 overflow-hidden text-ellipsis whitespace-nowrap py-1 text-sm`}
            >
              {UnitOfMeasure.quantityWithSuffix(
                productMasterData.get(item.product.pmdId || '')?.unitOfMeasure,
                (item.product.processedQuantity || item.product.quantity).toString(),
              )}
            </p>
          </Grid>
          <Grid item marginY={'auto'}>
            {iconForType(item)}
          </Grid>
        </Grid>
      ),
      sortValue: (item: ProductTransaction) => item.product.processedQuantity || item.product.quantity,
      columnWidth: 0.5,
    },
  ].filter(h => {
    if (h.key === 'company' && !isPartner) return false;
    if (h.key === 'destinationStockLocation' && currentCompany.id === Elteros.companyId) return false;
    return true;
  });

  const loadAll = async () => {
    loadAllProductTransactions();
    loadAllOrders();
  };

  const menuItems: MenuItemProps[] = useMemo(() => {
    return [
      {
        text: t(TranslationKey.export).singular.label,
        icon: <ExportIcon />,
        onClick: () => setExportModalOpen(true),
      },
      SettingsMenuItemTemplate(() => navigate(Routing.stock.history.settings.overview())),
      {
        text: t(TranslationKey.loadAll).singular.label,
        icon: <ImportIcon />,
        onClick: loadAll,
      },
    ];
  }, []);

  const filter = new ProductTransactionFilter(
    companyUsers,
    productMasterData,
    orders,
    tagRelations,
    containers,
    customFields,
    stockLocations,
    productTransactions,
    currentCompany,
  );

  return (
    <>
      <ProductTransactionExportModal open={exportModalOpen} setOpen={setExportModalOpen} />
      {selectedTransaction ? (
        <ProductTransactionDetailModal
          open={openDetailModal}
          setOpen={v => {
            setOpenDetailModal(v);
            setSelectedTransaction(null);
          }}
          transaction={selectedTransaction}
        />
      ) : null}

      <FlexPane
        header={
          <SearchBarWithFilter
            loading={productMasterDataLoading || productTransactionsLoading}
            items={items}
            setItems={setFilteredItems}
            placeholder={t(TranslationKey.filterProductTransactions).singular.label}
            menuItems={menuItems}
            filter={filter}
          />
        }
        content={
          <Table
            loading={productMasterDataLoading || ordersLoading || productTransactionsLoading}
            items={filteredItems}
            headers={headers}
            totalItemCount={items.length}
            filter={filter}
            onClick={item => {
              setSelectedTransaction(item);
              setOpenDetailModal(true);
            }}
          />
        }
      />
    </>
  );
}
